import { createSlice } from '@reduxjs/toolkit';

import {
    getAssessmentWorkloadOverview as getAssessmentWorkloadOverviewAPI,
    getAssessmentSummary as getAssessmentSummaryAPI,
    getReportsAnalytics as getReportsAnalyticsAPI
} from 'api/analytics';

const initialState = {
    assessessmentWorkloadOverview: {
        loading: true,
        error: '',
        totalAssessments: 0,
        totalPendingReview: 0,
        totalInProgress: 0,
        totalCompleted: 0,
        averageReviewTime: 0,
        overdueItems: []
    },
    assessmentSummary: {
        loading: true,
        error: '',
        totalAssessments: [],
        totalPendingActions: [],
        totalInProgress: [],
        totalMonthCompleted: []
    },
    reportsAnalytics: {
        loading: true,
        refreshing: false,
        error: '',
        summary: {
            totalAssessments: 0,
            inProgress: 0,
            completed: 0,
            appeals: 0
        },
        assessmentStatus: [],
        averageReviewTimeSeconds: null,
        overdueItems: 0,
        competencyOutcomes: [],
        averageCompletionTimeSeconds: null,
        evidenceSubmitted: [],
        guestEvidence: [],
        interviewAnalytics: {
            completed: 0,
            longestLengthSeconds: null,
            averageLengthSeconds: null,
            averageTranscriptWords: null,
            averagePassingRate: null
        }
    }
};

const slice = createSlice({
    name: 'analytics',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { key, loading = true } = action.payload;
            state[key].loading = loading;
        },
        setError(state, action) {
            const { key, error = '' } = action.payload;
            state[key].error = error;
        },
        setRefreshing(state, action) {
            const { key, refreshing = true } = action.payload;
            state[key].refreshing = refreshing;
        },
        setResult(state, action) {
            const { key, result } = action.payload;
            state[key] = {
                ...state[key],
                ...result
            };
        }
    }
});

export default slice.reducer;

export function getAssessmentWorkloadOverview(params = {}) {
    return async (dispatch) => {
        dispatch(
            slice.actions.setLoading({
                key: 'assessessmentWorkloadOverview',
                loading: true
            })
        );

        dispatch(
            slice.actions.setError({
                key: 'assessessmentWorkloadOverview',
                error: ''
            })
        );

        try {
            const response = await getAssessmentWorkloadOverviewAPI(params);
            dispatch(
                slice.actions.setResult({
                    key: 'assessessmentWorkloadOverview',
                    result: response?.data
                })
            );
            return response;
        } catch (err) {
            dispatch(
                slice.actions.setError({
                    key: 'assessessmentWorkloadOverview',
                    error: err?.message || 'Unable to get assessment workload overview.'
                })
            );
            throw err;
        } finally {
            dispatch(
                slice.actions.setLoading({
                    key: 'assessessmentWorkloadOverview',
                    loading: false
                })
            );
        }
    };
}

export function getReportsAnalytics(params = {}) {
    return async (dispatch, getState) => {
        const hasLoaded = !getState()?.analytics?.reportsAnalytics?.loading;

        dispatch(
            hasLoaded
                ? slice.actions.setRefreshing({
                      key: 'reportsAnalytics',
                      refreshing: true
                  })
                : slice.actions.setLoading({
                      key: 'reportsAnalytics',
                      loading: true
                  })
        );

        dispatch(
            slice.actions.setError({
                key: 'reportsAnalytics',
                error: ''
            })
        );

        try {
            const response = await getReportsAnalyticsAPI(params);
            dispatch(
                slice.actions.setResult({
                    key: 'reportsAnalytics',
                    result: response?.data
                })
            );
            return response;
        } catch (err) {
            dispatch(
                slice.actions.setError({
                    key: 'reportsAnalytics',
                    error: err?.message || 'Unable to get reports analytics.'
                })
            );
            throw err;
        } finally {
            dispatch(
                slice.actions.setLoading({
                    key: 'reportsAnalytics',
                    loading: false
                })
            );
            dispatch(
                slice.actions.setRefreshing({
                    key: 'reportsAnalytics',
                    refreshing: false
                })
            );
        }
    };
}

export function getAssessmentSummary(params = {}) {
    return async (dispatch) => {
        dispatch(
            slice.actions.setLoading({
                key: 'assessmentSummary',
                loading: true
            })
        );

        dispatch(
            slice.actions.setError({
                key: 'assessmentSummary',
                error: ''
            })
        );

        try {
            const response = await getAssessmentSummaryAPI(params);
            dispatch(
                slice.actions.setResult({
                    key: 'assessmentSummary',
                    result: response?.data
                })
            );
            return response;
        } catch (err) {
            dispatch(
                slice.actions.setError({
                    key: 'assessmentSummary',
                    error: err?.message || 'Unable to get assessment summary.'
                })
            );
            throw err;
        } finally {
            dispatch(
                slice.actions.setLoading({
                    key: 'assessmentSummary',
                    loading: false
                })
            );
        }
    };
}
